import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inputCls, btnPrimary, label } from "@/lib/ui";
import { ZipInput } from "@/components/ZipInput";

const Shell = ({ title, sub, children }) => (
  <div className="max-w-md mx-auto py-6 sm:py-12">
    <div className="mb-8">
      <span className="h-11 w-11 rounded-2xl bg-blue-600 text-white flex items-center justify-center font-black text-xl mb-5">F</span>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{title}</h1>
      <p className="text-slate-500 mt-2">{sub}</p>
    </div>
    {children}
  </div>
);

export default function AuthPage({ mode }) {
  const isReg = mode === "register";
  const { signIn } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ email: "", password: "", first_name: "", zip: "" });
  const [err, setErr] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const body = isReg ? form : { email: form.email, password: form.password, device_token: localStorage.getItem("ft_device") || null };
      const { data } = await api.post(isReg ? "/auth/register" : "/auth/login", body);
      if (isReg) {
        setSent(data.message);
        return;
      }
      signIn(data);
      toast.success("Signed in");
      nav(loc.state?.from || "/", { replace: true });
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Shell title="Check your email" sub="Less back-and-forth. More sold.">
        <p data-testid="register-check-email" className="text-sm text-slate-700 bg-blue-50 border border-blue-200 rounded-xl p-4">{sent}</p>
        <p className="text-sm text-slate-600 mt-6 text-center">
          <Link to="/login" data-testid="check-email-signin-link" className="font-semibold text-blue-600">Sign in</Link> · <Link to="/forgot-password" className="font-semibold text-blue-600">Forgot password?</Link>
        </p>
      </Shell>
    );
  }

  return (
    <Shell title={isReg ? "Join FollowThrough" : "Welcome back"} sub="Less back-and-forth. More sold.">
      <form onSubmit={submit} className="space-y-4" data-testid={isReg ? "register-form" : "login-form"}>
        {isReg && (
          <div><span className={label}>First name</span><input data-testid="register-firstname-input" required value={form.first_name} onChange={set("first_name")} className={inputCls} /></div>
        )}
        <div><span className={label}>Email</span><input data-testid="auth-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} autoComplete="email" /></div>
        <div><span className={label + " flex justify-between"}>Password {isReg && "(8+ characters)"}{!isReg && <Link to="/forgot-password" data-testid="forgot-password-link" className="text-blue-600">Forgot password?</Link>}</span><input data-testid="auth-password-input" type="password" required minLength={isReg ? 8 : 1} value={form.password} onChange={set("password")} className={inputCls} /></div>
        {isReg && (
          <div>
            <span className={label}>Your area (ZIP)</span>
            <ZipInput testid="register-zip-input" value={form.zip} onChange={(zip) => setForm({ ...form, zip })} />
            <p className="text-xs text-slate-500 mt-1">Any US ZIP code. Only your city is shown publicly — never your address.</p>
          </div>
        )}
        {err && <p data-testid="auth-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{err}</p>}
        <button data-testid="auth-submit-btn" disabled={busy} className={btnPrimary + " w-full"}>{busy ? "Please wait…" : isReg ? "Create account" : "Sign in"}</button>
      </form>
      <p className="text-sm text-slate-600 mt-6 text-center">
        {isReg ? "Already have an account? " : "New here? "}
        <Link data-testid="auth-switch-link" to={isReg ? "/login" : "/register"} state={loc.state} className="font-semibold text-blue-600">{isReg ? "Sign in" : "Create an account"}</Link>
      </p>
    </Shell>
  );
}
